'use client';

import {
  Dim,
  RadioGroup,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  Typography,
} from '@musinsa/mds';
import { useEffect, useMemo, useState } from 'react';
import type {
  OptionSheetConfirm,
  OptionSheetExtra,
  OptionSheetProduct,
} from '../adapters/seedingProductOptionsAdapter';
import { resolveSku, valueAvailability } from '../adapters/seedingProductOptionsAdapter';
import type { ProductOptionItem } from '../types';
import { OptionDropdown } from './OptionDropdown';
import { OptionSheetFooter } from './OptionSheetFooter';
import { ProductRadioItem } from './ProductRadioItem';
import { ProductSummaryRow } from './ProductSummaryRow';

/**
 * 시딩 상품 옵션 시트 — 옵션 종류(색상/사이즈)와 무관하게 **option group depth(1뎁스/2뎁스)** 만큼
 * dropdown 을 그린다. 그룹명(`optionName`)을 placeholder 로 그대로 사용한다. 2뎁스는 **순서 무관
 * 양방향** — 어느 그룹을 먼저 골라도 나머지 그룹의 노출/품절을 SKU 기준으로 계산하고, 선택이 충돌
 * (유효 SKU 없음)하면 해당 그룹만 해제한다. dropdown 은 항상 활성(잠금 없음). 추가옵션(무신사)은 별도.
 *
 * - 단일상품(`kind:'single'`): 곧바로 옵션 선택.
 * - 다중상품(`kind:'multi'`): 상품 선택 → 옵션 선택.
 */

export type SeedingProductOptionSheetData =
  | { kind: 'single'; product: OptionSheetProduct }
  | { kind: 'multi'; products: OptionSheetProduct[] };

export interface SeedingProductOptionSheetProps {
  open: boolean;
  onClose: () => void;
  data: SeedingProductOptionSheetData;
  onConfirm: (payload: OptionSheetConfirm) => void;
  /** 썸네일 비율 — 29CM=square(5:5), 무신사=portrait(세로). 서비스별 상이. (SID-4427) */
  imageRatio?: 'square' | 'portrait';
  className?: string;
}

const formatPrice = (price: number) => `${price.toLocaleString('ko-KR')}원`;

const DragHandle = () => (
  <div className="flex shrink-0 justify-center px-[160px]! pt-[8px]! pb-[8px]!">
    <div className="h-[4px] w-[36px] rounded-[10px] bg-[#e0e0e0]" />
  </div>
);

const SheetTitle = ({ title }: { title: string }) => (
  <div className="px-[16px]! pb-[9px]!">
    <Typography variant="textEtc16Med" colorScheme="black">
      {title}
    </Typography>
  </div>
);

/**
 * 옵션값 색상 swatch — 협의(2026-06): 색 표현은 `imageUrl` 만 사용한다.
 * BE `color` 는 hex 가 아니라 컬러 코드("2" 등)라 직접 칠할 수 없다.
 * - imageUrl 있음 → 이미지 칩.
 * - imageUrl 없고 color(코드)만 있음 → 흰색 swatch 로 폴백.
 * - 둘 다 없음 → swatch 미표기(null).
 */
const ColorSwatch = ({ imageUrl, color }: { imageUrl?: string; color?: string }) => {
  if (imageUrl) {
    return (
      <img
        aria-hidden
        src={imageUrl}
        alt=""
        className="inline-block h-[16px] w-[16px] rounded-full border border-gray-200 object-cover"
      />
    );
  }
  if (color) {
    // color 코드만 존재 — 흰색 swatch 폴백.
    return (
      <span
        aria-hidden
        className="inline-block h-[16px] w-[16px] rounded-full border border-gray-200 bg-white"
      />
    );
  }
  return null;
};

export const SeedingProductOptionSheet = ({
  open,
  onClose,
  data,
  onConfirm,
  imageRatio = 'portrait',
  className,
}: SeedingProductOptionSheetProps) => {
  const products = data.kind === 'multi' ? data.products : [data.product];
  const isMulti = data.kind === 'multi';

  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    isMulti ? null : (products[0]?.productId ?? null),
  );
  const [step, setStep] = useState<'product' | 'options'>(isMulti ? 'product' : 'options');

  // 시트 닫힐 때 초기화.
  // biome-ignore lint/correctness/useExhaustiveDependencies: open 토글 시에만 리셋
  useEffect(() => {
    if (!open) {
      setStep(isMulti ? 'product' : 'options');
      setSelectedProductId(isMulti ? null : (products[0]?.productId ?? null));
    }
  }, [open, isMulti]);

  const activeProduct = products.find((product) => product.productId === selectedProductId) ?? null;

  // 옵션 없는 단품(옵션그룹·sku·추가옵션 전부 없음) — 2depth(옵션 선택) 자체가 불필요. (CMQA-7182, SID-4296)
  const activeOptionless =
    activeProduct != null &&
    activeProduct.optionGroups.length === 0 &&
    activeProduct.skus.length === 0 &&
    activeProduct.extraOptions.length === 0;

  // 상품 선택 후: 단품이면 옵션 단계를 건너뛰고 바로 확정(goodsOptionNo 미설정), 아니면 옵션 단계로.
  const handlePickNext = () => {
    if (!activeProduct) return;
    if (activeOptionless) {
      onConfirm({ selected: { goodsNo: activeProduct.goodsNo, extraOptionNos: [] }, label: '' });
      return;
    }
    setStep('options');
  };

  return (
    <Sheet direction="bottom" open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetPortal>
        {/* open 시 Radix 가 시트 내부 첫 포커서블로 auto-focus 하며 scrollIntoView 로
            배경 window 를 스크롤시켜(body lock 적용 직전) 화면이 튀는 문제를 막는다. */}
        <SheetContent onPointerDownOutside={onClose} onOpenAutoFocus={(e) => e.preventDefault()}>
          <SheetMotion direction="bottom">
            <div
              className={`flex max-h-[584px] flex-col bg-white rounded-t-[8px] ${className ?? ''}`}
            >
              {/* 핸들 — 고정 상단 영역. */}
              <DragHandle />
              {/* 핸들~CTA 사이(헤더 + 목록/옵션)는 각 단계의 스크롤 영역에서 함께 스크롤된다.
                  하단 CTA(푸터)만 형제 shrink-0 으로 고정한다(sticky 아님). */}
              {step === 'product' ? (
                <ProductPickStep
                  products={products}
                  selectedProductId={selectedProductId}
                  onSelect={setSelectedProductId}
                  onNext={handlePickNext}
                  nextLabel={activeOptionless ? '선택 완료' : '선택하기'}
                  imageRatio={imageRatio}
                />
              ) : activeProduct ? (
                <OptionConfigureStep
                  key={activeProduct.productId}
                  product={activeProduct}
                  allowBack={isMulti}
                  imageRatio={imageRatio}
                  onBack={() => setStep('product')}
                  onConfirm={onConfirm}
                />
              ) : null}
            </div>
          </SheetMotion>
        </SheetContent>
        <SheetDimMotion>
          <Dim />
        </SheetDimMotion>
      </SheetPortal>
    </Sheet>
  );
};

/* ── 상품 선택 단계 (다중상품) ──────────────────────────────────── */

interface ProductPickStepProps {
  products: OptionSheetProduct[];
  selectedProductId: string | null;
  onSelect: (productId: string) => void;
  onNext: () => void;
  /** 선택 CTA 라벨 — 단품(옵션 없음)은 옵션 단계 없이 바로 확정이라 '선택 완료'. */
  nextLabel: string;
  imageRatio: 'square' | 'portrait';
}

const ProductPickStep = ({
  products,
  selectedProductId,
  onSelect,
  onNext,
  nextLabel,
  imageRatio,
}: ProductPickStepProps) => (
  <>
    <div className="min-h-0 flex-1 overflow-y-auto">
      <SheetTitle title="상품 선택" />
      <RadioGroup value={selectedProductId ?? ''} onValueChange={onSelect}>
        {products.map((product) => (
          <ProductRadioItem
            key={product.productId}
            value={product.productId}
            brand={product.brand}
            productName={product.name}
            price={formatPrice(product.price)}
            imageUrl={product.imageUrl}
            imageRatio={imageRatio}
            soldOut={product.soldOut}
          />
        ))}
      </RadioGroup>
    </div>
    <div className="shrink-0">
      <OptionSheetFooter
        variant="single-cta"
        primary={{ label: nextLabel, onClick: onNext, disabled: !selectedProductId }}
      />
    </div>
  </>
);

/* ── 옵션 선택 단계 (depth 기반) ────────────────────────────────── */

interface OptionConfigureStepProps {
  product: OptionSheetProduct;
  allowBack: boolean;
  imageRatio: 'square' | 'portrait';
  onBack: () => void;
  onConfirm: (payload: OptionSheetConfirm) => void;
}

const OptionConfigureStep = ({
  product,
  allowBack,
  imageRatio,
  onBack,
  onConfirm,
}: OptionConfigureStepProps) => {
  const { optionGroups, skus, extraOptions } = product;

  // group 별 선택 valueId (없으면 null). 순서 무관 — 어느 그룹이든 먼저 선택 가능(양방향).
  const [groupSelections, setGroupSelections] = useState<(number | null)[]>(
    optionGroups.map(() => null),
  );
  // 추가옵션 선택 (extraOptionId → valueId).
  const [extraSelections, setExtraSelections] = useState<Record<number, number | null>>({});

  const handleGroupChange = (index: number, valueId: number) => {
    setGroupSelections((prev) => {
      const next = [...prev];
      next[index] = valueId;
      // 양방향 cascade — 이번 선택으로 다른 그룹의 기존 선택이 유효 SKU 조합을 잃으면 그 그룹만 해제.
      // (앞→뒤 일방 리셋이 아니라, 충돌나는 그룹만 해제. 안정화까지 반복.)
      let changed = true;
      while (changed) {
        changed = false;
        for (let j = 0; j < next.length; j += 1) {
          if (j === index || next[j] == null) continue;
          const others = next.filter((v, k): v is number => k !== j && v != null);
          if (!valueAvailability(skus, others, next[j] as number).available) {
            next[j] = null;
            changed = true;
          }
        }
      }
      return next;
    });
  };

  const allGroupsSelected = groupSelections.every((value) => value != null);
  const requiredExtrasSelected = extraOptions
    .filter((extra) => extra.required)
    .every((extra) => extraSelections[extra.extraOptionId] != null);

  const sku = useMemo(
    () => (allGroupsSelected ? resolveSku(skus, groupSelections as number[]) : null),
    [allGroupsSelected, groupSelections, skus],
  );
  const canConfirm = allGroupsSelected && requiredExtrasSelected && sku != null && !sku.outOfStock;

  const handleConfirm = () => {
    if (!sku) return;
    const groupLabels = optionGroups.map(
      (group, index) =>
        group.values.find((value) => value.valueId === groupSelections[index])?.label ?? '',
    );
    const extraLabels = extraOptions
      .map((extra) => {
        const valueId = extraSelections[extra.extraOptionId];
        return valueId != null
          ? (extra.values.find((value) => value.valueId === valueId)?.label ?? null)
          : null;
      })
      .filter((label): label is string => label != null);
    const extraOptionNos = extraOptions
      .map((extra) => extraSelections[extra.extraOptionId])
      .filter((valueId): valueId is number => valueId != null);

    onConfirm({
      selected: { goodsNo: product.goodsNo, goodsOptionNo: sku.skuId, extraOptionNos },
      label: [...groupLabels.filter(Boolean), ...extraLabels].join(' · '),
    });
  };

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <ProductSummaryRow
          brand={product.brand}
          productName={product.name}
          price={formatPrice(product.price)}
          imageUrl={product.imageUrl}
          imageRatio={imageRatio}
        />
        {/* 상품 요약 ↔ 드롭다운 간격 12px (요약 py-8 + 여기 pt-4). (SID-4427) */}
        <div className="flex flex-col px-[16px]! pt-[4px]! pb-[12px]! gap-[8px]!">
          {optionGroups.map((group, index) => {
            // 나(이 그룹)를 제외한 다른 그룹의 선택값. 순서 무관 양방향 유효성 계산에 사용.
            const others = groupSelections.filter((v, k): v is number => k !== index && v != null);
            // 색상 칩 노출 여부 — hex 든 imageUrl 이든 하나라도 있으면 swatch 를 그린다.
            const hasColor = group.values.some((value) => value.color || value.imageUrl);

            // 다른 그룹 선택 하에서 노출 가능한 value 만(유효 SKU 조합 밖 제외) + 품절 표시.
            const options: ProductOptionItem[] = group.values
              .map((value) => ({ value, ...valueAvailability(skus, others, value.valueId) }))
              .filter((entry) => entry.available)
              .map((entry) => ({
                code: String(entry.value.valueId),
                label: entry.value.label,
                hex: entry.value.color,
                imageUrl: entry.value.imageUrl,
                soldOut: entry.soldOut,
              }));

            const selectedCode = groupSelections[index];
            return (
              <OptionDropdown
                key={group.optionName}
                placeholder={group.optionName}
                value={selectedCode != null ? String(selectedCode) : null}
                options={options}
                onChange={(code) => handleGroupChange(index, Number(code))}
                renderTriggerValue={
                  hasColor
                    ? (option) => (
                        <div className="flex items-center gap-[8px]">
                          <ColorSwatch imageUrl={option.imageUrl} color={option.hex} />
                          <Typography variant="body14pxMed" colorScheme="black">
                            {option.label}
                          </Typography>
                        </div>
                      )
                    : undefined
                }
                renderOptionLabel={
                  hasColor
                    ? (option) => (
                        <div className="flex items-center gap-[8px]">
                          <ColorSwatch imageUrl={option.imageUrl} color={option.hex} />
                          {/* 목록 라벨 13px/#666666(gray-600). (SID-4429) */}
                          <Typography
                            variant="body13pxReg"
                            colorScheme={option.soldOut ? 'gray-500' : 'gray-600'}
                          >
                            {option.label}
                            {option.soldOut && '(품절)'}
                          </Typography>
                        </div>
                      )
                    : undefined
                }
              />
            );
          })}

          {extraOptions.map((extra) => (
            <ExtraOptionDropdown
              key={extra.extraOptionId}
              extra={extra}
              value={extraSelections[extra.extraOptionId] ?? null}
              onChange={(valueId) =>
                setExtraSelections((prev) => ({ ...prev, [extra.extraOptionId]: valueId }))
              }
            />
          ))}
        </div>
      </div>
      <div className="shrink-0">
        <OptionSheetFooter
          variant={allowBack ? 'dual-cta' : 'single-cta'}
          secondary={allowBack ? { label: '상품 다시 선택', onClick: onBack } : undefined}
          primary={{ label: '선택 완료', onClick: handleConfirm, disabled: !canConfirm }}
        />
      </div>
    </>
  );
};

/* ── 추가옵션 dropdown (무신사) ─────────────────────────────────── */

const formatExtraLabel = (label: string, extraPrice: number) =>
  extraPrice > 0 ? `${label} (+${extraPrice.toLocaleString('ko-KR')}원)` : label;

interface ExtraOptionDropdownProps {
  extra: OptionSheetExtra;
  value: number | null;
  onChange: (valueId: number) => void;
}

const ExtraOptionDropdown = ({ extra, value, onChange }: ExtraOptionDropdownProps) => {
  const options: ProductOptionItem[] = extra.values.map((extraValue) => ({
    code: String(extraValue.valueId),
    label: formatExtraLabel(extraValue.label, extraValue.extraPrice),
    soldOut: extraValue.soldOut,
  }));
  return (
    <OptionDropdown
      placeholder={extra.required ? extra.name : `${extra.name} (선택)`}
      value={value != null ? String(value) : null}
      options={options}
      onChange={(code) => onChange(Number(code))}
    />
  );
};
